import { LitElement, html, type TemplateResult } from "lit";
import { customElement, property } from "lit/decorators.js";

type LegalPage = "terms" | "privacy";

@customElement("katanoid-terms")
export class KatanoidTerms extends LitElement {
  protected override createRenderRoot(): HTMLElement {
    return this;
  }

  @property({ type: String }) page: LegalPage = "terms";

  protected override render(): TemplateResult {
    return html`
      <div class="legal">
        <div class="legal-content">
          <button
            class="btn btn--ghost btn--sm legal-back"
            @click=${() => this.dispatchEvent(new CustomEvent("back", { bubbles: true, composed: true }))}
          >&larr; Back</button>

          ${this.page === "terms" ? this.renderTerms() : this.renderPrivacy()}
        </div>
      </div>
    `;
  }

  // ── Terms of Service ──

  private renderTerms(): TemplateResult {
    return html`
      <h1 class="legal-title">Terms of Service</h1>
      <p class="legal-updated">Last updated: March 6, 2026</p>

      <section class="legal-section">
        <p>
          Katanoid is a service operated by EXYPNI LTD, a company registered in England and Wales
          (company number 17075298), registered office at 71-75 Shelton Street, Covent Garden, London, WC2H 9JQ.
        </p>
      </section>

      <section class="legal-section">
        <h2>1. Acceptance of Terms</h2>
        <p>
          By creating an account or using Katanoid ("the Service"), you agree to be bound by these Terms of Service
          ("Terms") and our <a href="/privacy" @click=${this._navigateTo("/privacy")}>Privacy Policy</a>, which explains how we
          collect, use, and protect your personal data. If you do not agree to these Terms or the Privacy Policy,
          do not use the Service. Your continued use of the Service constitutes acceptance of any updates to these Terms.
        </p>
      </section>

      <section class="legal-section">
        <h2>2. Eligibility</h2>
        <p>
          You must be at least <strong>16 years of age</strong> to use the Service. By creating an account, you represent
          and warrant that you meet this age requirement. If you are between 16 and 18 years of age, you represent that
          your parent or legal guardian has reviewed and agreed to these Terms on your behalf.
        </p>
        <p>
          We reserve the right to request verification of age at any time and to terminate accounts that do not meet
          the minimum age requirement.
        </p>
      </section>

      <section class="legal-section">
        <h2>3. Account Responsibilities</h2>
        <p>
          You are responsible for maintaining the confidentiality of your account credentials and for all activity that
          occurs under your account. You agree to notify us immediately of any unauthorized use.
        </p>
        <p>
          You may not share, transfer, or sell access to your account. Each account is for a single individual user.
        </p>
      </section>

      <section class="legal-section">
        <h2>4. Service Description</h2>
        <p>
          Katanoid is an AI-powered assistant platform that integrates with various messaging, email, calendar, and
          other third-party services. The Service is under <strong>active development</strong> and features, capabilities,
          and integrations may change, be added, or be removed at any time without prior notice.
        </p>
        <p>
          The Service may perform actions on your behalf, including but not limited to sending messages, creating
          calendar events, and modifying content in connected third-party services. By using the Service, you authorize
          these actions and acknowledge that you are solely responsible for reviewing, confirming, and accepting the
          consequences of any action the AI performs on your behalf.
        </p>
      </section>

      <section class="legal-section">
        <h2>5. Service Availability &amp; Reliability</h2>
        <p>
          The Service is provided on an <strong>"as is" and "as available"</strong> basis. We do not guarantee that
          the Service will be uninterrupted, error-free, or free of bugs, defects, or security vulnerabilities.
        </p>
        <p>As a platform under active development, you acknowledge that:</p>
        <ul>
          <li>Features may contain bugs or produce unexpected results</li>
          <li>AI-generated responses may be inaccurate, incomplete, or inappropriate</li>
          <li>Integrations with third-party services may break or change without notice</li>
          <li>Data processing and message delivery may experience delays or failures</li>
          <li>The Service may undergo maintenance or downtime at any time</li>
        </ul>
      </section>

      <section class="legal-section">
        <h2>6. AI-Generated Content</h2>
        <p>
          The Service uses artificial intelligence to generate responses and perform actions. AI-generated content may
          be inaccurate, misleading, or incomplete. You should not rely on AI-generated content for critical decisions
          including, but not limited to, medical, legal, financial, or safety-related matters.
        </p>
        <p>
          You are solely responsible for reviewing and verifying any AI-generated content before acting on it.
        </p>
      </section>

      <section class="legal-section">
        <h2>7. Acceptable Use</h2>
        <p>You agree not to use the Service to:</p>
        <ul>
          <li>Violate any applicable laws or regulations</li>
          <li>Send spam, phishing, or other unsolicited messages</li>
          <li>Harass, abuse, or harm other individuals</li>
          <li>Generate or distribute harmful, illegal, or objectionable content</li>
          <li>Attempt to circumvent rate limits, access controls, or security measures</li>
          <li>Reverse engineer, decompile, or attempt to extract the source code of the Service</li>
          <li>Use the Service to build a competing product or service</li>
          <li>Impersonate any person or entity</li>
        </ul>
        <p>We reserve the right to suspend or terminate your account for violations of these terms.</p>
      </section>

      <section class="legal-section">
        <h2>8. Third-Party Integrations</h2>
        <p>
          The Service integrates with third-party platforms (e.g., WhatsApp, Telegram, Discord, Google Calendar, etc.).
          Your use of these integrations is subject to the respective third-party terms and privacy policies. We are
          not responsible for the availability, accuracy, or conduct of third-party services.
        </p>
      </section>

      <section class="legal-section">
        <h2>9. Intellectual Property</h2>
        <p>
          The Service, its original content, features, and functionality are owned by EXYPNI LTD and are protected by
          intellectual property laws. You retain ownership of content you submit through the Service.
        </p>
        <p>
          By using the Service, you grant us a limited license to process your content solely for the purpose of
          providing the Service to you.
        </p>
      </section>

      <section class="legal-section">
        <h2>10. Copyright Complaints</h2>
        <p>
          If you believe that content available through the Service infringes your intellectual property rights,
          please contact us at <a href="mailto:legal@katanoid.com">legal@katanoid.com</a> with the following
          information: a description of the copyrighted work, the location of the infringing content, your contact
          details, and a statement that you have a good faith belief that the use is unauthorized. We will investigate
          and respond within 14 business days.
        </p>
      </section>

      <section class="legal-section">
        <h2>11. Payment &amp; Subscriptions</h2>
        <p>
          Some features require a paid subscription. Subscription fees are billed in advance on a recurring basis.
          You may cancel at any time, but refunds are not provided for partial billing periods.
        </p>
        <p>
          We reserve the right to change pricing with 30 days' notice via email. Continued use after a price change
          constitutes acceptance of the new pricing.
        </p>
      </section>

      <section class="legal-section">
        <h2>12. Limitation of Liability</h2>
        <p>
          To the fullest extent permitted by applicable law, EXYPNI LTD and its officers, directors, employees, and
          agents shall not be liable for any indirect, incidental, special, consequential, or punitive damages,
          including but not limited to loss of profits, data, use, or goodwill, arising out of or in connection
          with your use of the Service.
        </p>
        <p>
          In no event shall our total liability exceed the amount you have paid us in the twelve (12) months
          preceding the claim, or fifty US dollars ($50), whichever is greater.
        </p>
        <p>Without limiting the foregoing, we are not liable for any damages, losses, or consequences resulting from:</p>
        <ul>
          <li>Bugs, errors, or unexpected behavior in the Service</li>
          <li>Inaccurate or misleading AI-generated content</li>
          <li>Service interruptions, downtime, or data loss</li>
          <li>Actions taken by the AI assistant on your behalf</li>
          <li>Third-party integration failures or changes</li>
          <li>Unauthorized access to your account due to your failure to secure credentials</li>
        </ul>
        <p>
          Nothing in these Terms excludes or limits liability for death or personal injury caused by negligence,
          fraud or fraudulent misrepresentation, or any other liability that cannot be excluded or limited under
          the laws of England and Wales, including your statutory rights under the Consumer Rights Act 2015.
        </p>
      </section>

      <section class="legal-section">
        <h2>13. Indemnification</h2>
        <p>
          You agree to indemnify and hold harmless EXYPNI LTD from any claims, damages, losses, or expenses
          (including reasonable legal fees) arising from your use of the Service, your violation of these Terms,
          or your violation of any rights of a third party.
        </p>
      </section>

      <section class="legal-section">
        <h2>14. Termination</h2>
        <p>
          We may suspend or terminate your access at any time, with or without cause, and with or without notice.
          Upon termination, your right to use the Service ceases immediately. We are not liable for any loss
          resulting from termination.
        </p>
        <p>You may delete your account at any time through the Settings page.</p>
      </section>

      <section class="legal-section">
        <h2>15. Disclaimer of Warranties</h2>
        <p>
          THE SERVICE IS PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND, WHETHER EXPRESS OR IMPLIED, INCLUDING
          BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND
          NON-INFRINGEMENT.
        </p>
        <p>
          Nothing in these Terms excludes or limits liability for death or personal injury caused by negligence,
          fraud or fraudulent misrepresentation, or any other liability that cannot be excluded or limited under
          the laws of England and Wales, including your statutory rights under the Consumer Rights Act 2015.
        </p>
      </section>

      <section class="legal-section">
        <h2>16. Force Majeure</h2>
        <p>
          We shall not be liable for any failure or delay in performing our obligations where such failure or delay
          results from circumstances beyond our reasonable control, including but not limited to third-party API or
          service outages, internet service disruptions, power failures, natural disasters, pandemics, government
          actions, or acts of war or terrorism.
        </p>
      </section>

      <section class="legal-section">
        <h2>17. Dispute Resolution</h2>
        <p>
          If you are located outside England and Wales, any dispute arising out of or relating to these Terms shall
          be resolved by binding arbitration administered under the rules of the London Court of International
          Arbitration (LCIA). You agree to waive any right to participate in a class action or class-wide arbitration.
          This clause does not apply to users in the UK, who retain their statutory right to bring claims in the
          courts of England and Wales.
        </p>
      </section>

      <section class="legal-section">
        <h2>18. Governing Law</h2>
        <p>
          These Terms shall be governed by and construed in accordance with the laws of England and Wales. Any
          disputes arising out of or in connection with these Terms shall be subject to the exclusive jurisdiction
          of the courts of England and Wales. If you are a consumer in the EU, you retain any mandatory protections
          provided by the law of your country of residence.
        </p>
      </section>

      <section class="legal-section">
        <h2>19. Changes to Terms</h2>
        <p>
          We reserve the right to modify these Terms at any time. Material changes will be communicated via email
          at least 30 days before they take effect. Your continued use of the Service after changes take effect
          constitutes acceptance of the updated Terms. If you do not agree with the updated Terms, you may delete
          your account.
        </p>
      </section>

      <section class="legal-section">
        <h2>20. Notices</h2>
        <p>
          All notices to you under these Terms will be sent via email to the address associated with your account.
          Notices are deemed received at the time the email is sent. It is your responsibility to keep your email
          address up to date.
        </p>
      </section>

      <section class="legal-section">
        <h2>21. Severability</h2>
        <p>
          If any provision of these Terms is found to be invalid or unenforceable by a court of competent
          jurisdiction, that provision shall be enforced to the maximum extent permissible, and the remaining
          provisions shall remain in full force and effect.
        </p>
      </section>

      <section class="legal-section">
        <h2>22. Waiver</h2>
        <p>
          Our failure or delay in exercising any right or remedy under these Terms does not constitute a waiver
          of that right or remedy.
        </p>
      </section>

      <section class="legal-section">
        <h2>23. Assignment</h2>
        <p>
          We may assign, transfer, or sub-contract our rights and obligations under these Terms to any third party
          without your prior consent. You may not assign or transfer your rights or obligations without our written
          consent.
        </p>
      </section>

      <section class="legal-section">
        <h2>24. Entire Agreement</h2>
        <p>
          These Terms, together with our <a href="/privacy" @click=${this._navigateTo("/privacy")}>Privacy Policy</a>,
          constitute the entire agreement between you and EXYPNI LTD regarding your use of the Service and
          supersede all prior agreements and understandings.
        </p>
      </section>

      <section class="legal-section">
        <h2>25. Contact</h2>
        <p>
          If you have questions about these Terms, please contact us at
          <a href="mailto:legal@katanoid.com">legal@katanoid.com</a>.
        </p>
      </section>
    `;
  }

  // ── Privacy Policy ──

  private renderPrivacy(): TemplateResult {
    return html`
      <h1 class="legal-title">Privacy Policy</h1>
      <p class="legal-updated">Last updated: March 6, 2026</p>

      <section class="legal-section">
        <h2>1. Data Controller</h2>
        <p>
          The data controller responsible for your personal data is EXYPNI LTD, a company registered in England
          and Wales (company number 17075298), registered office at 71-75 Shelton Street, Covent Garden, London,
          WC2H 9JQ.
        </p>
        <p>For privacy-related inquiries, contact us at <a href="mailto:privacy@katanoid.com">privacy@katanoid.com</a>.</p>
      </section>

      <section class="legal-section">
        <h2>2. Information We Collect</h2>
        <p>We collect the following types of information:</p>
        <ul>
          <li><strong>Account information:</strong> Email address, display name, and authentication credentials</li>
          <li><strong>Usage data:</strong> Interaction logs, feature usage, and performance metrics</li>
          <li><strong>Message content:</strong> Messages you send and receive through integrated platforms, processed to provide the Service</li>
          <li><strong>Device information:</strong> Browser type, operating system, and device identifiers</li>
          <li><strong>Payment information:</strong> Processed securely by our payment provider; we do not store full payment details</li>
        </ul>
      </section>

      <section class="legal-section">
        <h2>3. Lawful Basis for Processing</h2>
        <p>We process your personal data on the following legal bases under UK GDPR Article 6:</p>
        <p>
          <strong>Contract performance (Article 6(1)(b)):</strong> We process your account information and message
          content because it is necessary to provide you with the Service. Without this processing, we cannot
          operate the Service for you.
        </p>
        <p>
          <strong>Legitimate interests (Article 6(1)(f)):</strong> We process usage data, device information, and
          interaction logs for the purposes of improving the Service, detecting abuse, ensuring security, and
          understanding how the Service is used. Our legitimate interest is maintaining a secure, functional service.
          We have assessed that this processing does not override your rights and freedoms.
        </p>
        <p>
          <strong>Legal obligation (Article 6(1)(c)):</strong> We may retain certain data where required by UK law,
          for example tax records related to payment transactions.
        </p>
        <p>
          <strong>Consent (Article 6(1)(a)):</strong> If we ever send you marketing communications, we will obtain
          your explicit consent first. You can withdraw consent at any time by contacting
          <a href="mailto:privacy@katanoid.com">privacy@katanoid.com</a>.
        </p>
      </section>

      <section class="legal-section">
        <h2>4. How We Use Your Information</h2>
        <p>We use your information to:</p>
        <ul>
          <li>Provide, maintain, and improve the Service</li>
          <li>Process your messages through AI models and connected integrations</li>
          <li>Send transactional communications (confirmations, security alerts)</li>
          <li>Detect and prevent abuse, fraud, and security incidents</li>
          <li>Comply with legal obligations</li>
        </ul>
      </section>

      <section class="legal-section">
        <h2>5. Data Sharing</h2>
        <p>We do not sell your personal information. We may share data with:</p>
        <ul>
          <li>
            <strong>AI model providers:</strong> Message content is sent to OpenRouter, a US-based API routing
            service, which forwards your messages to third-party large language model providers (such as OpenAI,
            Anthropic, and Google) to generate AI responses. This means your message content is processed by both
            OpenRouter and the underlying model provider.
          </li>
          <li>
            <strong>Integrated platforms:</strong> When you connect services (WhatsApp, Telegram, etc.), your data
            is shared as needed to provide those integrations.
          </li>
          <li><strong>Service providers:</strong> Infrastructure, analytics, and payment processing partners.</li>
          <li><strong>Legal requirements:</strong> When required by law or to protect our rights.</li>
        </ul>
        <p>
          If the Service or our website contains links to third-party websites or services, those sites have their
          own privacy policies. We are not responsible for their privacy practices and encourage you to review their
          policies before providing them with your data.
        </p>
      </section>

      <section class="legal-section">
        <h2>6. AI Provider Data Handling</h2>
        <p>
          To provide the Service, your message content is sent to OpenRouter, a US-based API routing service,
          which forwards your messages to third-party large language model providers (such as OpenAI, Anthropic,
          and Google) to generate AI responses.
        </p>
        <p>
          We have opted out of having your data used for model training wherever our provider agreements allow this.
          Your message content is sent for the sole purpose of generating responses and is not knowingly contributed
          to training datasets.
        </p>
        <p>
          If you want to know which specific model providers are currently in use, contact
          <a href="mailto:privacy@katanoid.com">privacy@katanoid.com</a>.
        </p>
      </section>

      <section class="legal-section">
        <h2>7. Sub-processors</h2>
        <p>We use the following categories of third-party service providers who process personal data on our behalf:</p>
        <ul>
          <li>
            <strong>AI/LLM providers:</strong> OpenRouter (US-based API routing service) and the underlying model
            providers it routes to, including OpenAI, Anthropic, and Google. These process message content to
            generate AI responses.
          </li>
          <li>
            <strong>Cloud infrastructure:</strong> Cloudflare (CDN, security, DNS), Fly.io (application hosting),
            Supabase (database and authentication).
          </li>
          <li>
            <strong>Payment processors:</strong> Handle subscription billing. We do not store your full payment details.
          </li>
          <li>
            <strong>Email delivery providers:</strong> Send transactional emails such as account confirmations
            and security alerts.
          </li>
        </ul>
        <p>
          Each sub-processor is bound by a data processing agreement. For a current list of specific sub-processors,
          contact <a href="mailto:privacy@katanoid.com">privacy@katanoid.com</a>.
        </p>
      </section>

      <section class="legal-section">
        <h2>8. International Data Transfers</h2>
        <p>
          Your personal data may be transferred to and processed in countries outside the United Kingdom. This
          happens because our AI model providers and cloud infrastructure providers operate servers in the United
          States and other countries.
        </p>
        <p>
          When we transfer your data outside the UK, we ensure it is protected by Standard Contractual Clauses
          (SCCs) approved by the UK Secretary of State for international data transfers. These are standard legal
          contracts that require the receiving party to protect your data to UK standards.
        </p>
        <p>
          You can request a copy of the relevant SCCs by contacting
          <a href="mailto:privacy@katanoid.com">privacy@katanoid.com</a>.
        </p>
      </section>

      <section class="legal-section">
        <h2>9. Data Retention</h2>
        <ul>
          <li>
            <strong>Message content:</strong> Retained for as long as your account is active. After you delete your
            account, all message content is permanently deleted within 90 days. You can request earlier deletion of
            your message content at any time without deleting your account by contacting
            <a href="mailto:privacy@katanoid.com">privacy@katanoid.com</a>.
          </li>
          <li>
            <strong>Account information (email, display name):</strong> Retained for as long as your account is
            active. Permanently deleted within 90 days of account deletion.
          </li>
          <li>
            <strong>Usage and interaction logs:</strong> Retained for 24 months from the date of collection, then
            permanently deleted.
          </li>
          <li>
            <strong>Payment records:</strong> Retained for 6 years after the transaction, as required by HMRC for
            tax purposes.
          </li>
          <li>
            <strong>Security and abuse logs:</strong> Retained for 24 months from the date of collection, then
            permanently deleted.
          </li>
        </ul>
      </section>

      <section class="legal-section">
        <h2>10. Data Security</h2>
        <p>
          We implement industry-standard security measures to protect your information. However, no method of
          transmission or storage is 100% secure, and we cannot guarantee absolute security.
        </p>
      </section>

      <section class="legal-section">
        <h2>11. Cookies and Tracking</h2>
        <p>We do not use advertising or profiling cookies on katanoid.com.</p>
        <p>The following technologies are used to operate the Service:</p>
        <ul>
          <li>
            <strong>Supabase authentication cookies:</strong> These keep you logged in. They persist until you sign out.
          </li>
          <li>
            <strong>Cloudflare cookies (such as __cf_bm):</strong> These are set automatically by Cloudflare for
            bot protection and security. They expire within 30 minutes of inactivity.
          </li>
          <li>
            <strong>Cloudflare Web Analytics:</strong> Used to understand aggregate traffic patterns such as page
            views and visitor counts. Cloudflare Web Analytics does not use cookies and does not collect personal
            data. No consent is required.
          </li>
        </ul>
        <p>
          The cookies listed above are strictly necessary to provide the Service or do not process personal data.
          We do not use any cookies for advertising, profiling, or cross-site tracking.
        </p>
      </section>

      <section class="legal-section">
        <h2>12. Your Rights</h2>
        <p>Under UK GDPR, you have the following rights:</p>
        <ul>
          <li><strong>Right to be informed:</strong> You have the right to know how your data is collected and used (this policy).</li>
          <li><strong>Right of access:</strong> You can request a copy of all personal data we hold about you.</li>
          <li><strong>Right to rectification:</strong> You can ask us to correct inaccurate personal data.</li>
          <li><strong>Right to erasure:</strong> You can ask us to delete your personal data. We will do so unless we have a legal obligation to retain it.</li>
          <li><strong>Right to restrict processing:</strong> You can ask us to temporarily stop processing your data while we resolve a complaint or verify accuracy.</li>
          <li><strong>Right to data portability:</strong> You can request your data in a structured, machine-readable format (JSON).</li>
          <li><strong>Right to object:</strong> You can object to processing based on legitimate interests. We will stop unless we have compelling legitimate grounds.</li>
          <li><strong>Rights related to automated decision-making:</strong> See the Automated Decision-Making section below.</li>
        </ul>
        <p>
          To exercise any of these rights, email <a href="mailto:privacy@katanoid.com">privacy@katanoid.com</a>.
          We will respond within 30 days. If we need more time (up to 60 additional days for complex requests),
          we will tell you within the initial 30-day period.
        </p>
        <p>
          You also have the right to lodge a complaint with the Information Commissioner's Office (ICO) if you
          believe we have handled your data unlawfully. You can contact the ICO at
          <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer">https://ico.org.uk</a> or write
          to: Information Commissioner's Office, Wycliffe House, Water Lane, Wilmslow, Cheshire, SK9 5AF.
        </p>
      </section>

      <section class="legal-section">
        <h2>13. Automated Decision-Making</h2>
        <p>
          The Service uses artificial intelligence to generate responses and perform actions based on your messages.
          This processing is automated.
        </p>
        <p>
          Currently, the Service does not make any automated decisions that produce legal effects or similarly
          significant effects on you (such as credit decisions, hiring decisions, or access to essential services).
          The AI generates suggestions and performs actions that you can review, accept, or reject.
        </p>
        <p>If this changes in the future, we will update this section and notify you by email.</p>
      </section>

      <section class="legal-section">
        <h2>14. Children's Privacy</h2>
        <p>
          The Service is not intended for children under 16 years of age. We do not knowingly collect personal
          information from children under 16. If we become aware that a child under 16 has provided us with
          personal information, we will take steps to delete such information.
        </p>
      </section>

      <section class="legal-section">
        <h2>15. EU/EEA Users</h2>
        <p>
          If you are located in the European Economic Area (EEA), your personal data is protected under the EU
          General Data Protection Regulation (EU GDPR). The rights described in this policy apply equally to you
          under EU GDPR.
        </p>
        <p>
          If our processing of EU/EEA residents' data reaches a scale that requires it under Article 27 of the
          EU GDPR, we will appoint an EU representative and update this section with their contact details.
        </p>
      </section>

      <section class="legal-section">
        <h2>16. California Users</h2>
        <p>
          If you are a California resident, you have the following rights under the California Consumer Privacy
          Act (CCPA):
        </p>
        <ul>
          <li>The right to know what personal information we collect, use, and disclose.</li>
          <li>The right to request deletion of your personal information.</li>
          <li>The right to opt out of the sale of your personal information.</li>
        </ul>
        <p>We do not sell your personal information.</p>
        <p>
          To exercise these rights, email <a href="mailto:privacy@katanoid.com">privacy@katanoid.com</a>.
          We will not discriminate against you for exercising your CCPA rights.
        </p>
      </section>

      <section class="legal-section">
        <h2>17. Changes to This Policy</h2>
        <p>
          We may update this Privacy Policy from time to time. If we make material changes, we will notify you
          by email at least 30 days before the changes take effect. Your continued use of the Service after the
          changes take effect constitutes acceptance. If you do not agree with the updated policy, you may delete
          your account.
        </p>
      </section>

      <section class="legal-section">
        <h2>18. Contact</h2>
        <p>
          For privacy-related inquiries, contact us at
          <a href="mailto:privacy@katanoid.com">privacy@katanoid.com</a>.
        </p>
      </section>
    `;
  }

  // ── Handlers ──

  private _navigateTo = (path: string) => (e: Event): void => {
    e.preventDefault();
    window.history.pushState(null, "", path);
    window.dispatchEvent(new PopStateEvent("popstate"));
  };
}

declare global {
  interface HTMLElementTagNameMap {
    "katanoid-terms": KatanoidTerms;
  }
}
